import { useCallback, useMemo } from "react";
import type { EditorEvents } from "@tiptap/react";
import { TipTapEditor } from "@/react/components/shared/BuilderUI/Editor/fields/RichTextField/TipTapEditor";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import {
  convertTipTapToHTML,
  createTipTapFromText,
  isPlainText,
  isRichTextContent,
} from "../../utils/richTextHelpers";
import { useTextBlockUpdate } from "../../utils/useTextBlockUpdate";
import type { TextRendererProps } from "./TextRenderer.types";

export const BuilderTextRenderer = ({
  blockPath,
  text = "",
  className,
  placeholder,
}: TextRendererProps) => {
  const { shouldAllowInlineEditing, updateTextProp } =
    useTextBlockUpdate(blockPath);
  const isInlineEditing = useBuilderStore(state => state.isInlineEditing);
  const startInlineEditing = useBuilderStore(state => state.startInlineEditing);
  const stopInlineEditing = useBuilderStore(state => state.stopInlineEditing);
  const isCurrentlyInlineEditing = isInlineEditing(blockPath);

  const handleDoubleClick = useCallback(() => {
    if (!shouldAllowInlineEditing) {
      return;
    }
    if (isPlainText(text)) {
      const tipTapContent = createTipTapFromText(text);
      updateTextProp(tipTapContent, "rich-text");
    }
    startInlineEditing(blockPath);
  }, [
    shouldAllowInlineEditing,
    text,
    updateTextProp,
    startInlineEditing,
    blockPath,
  ]);

  const handleBlur = useCallback(
    (props: EditorEvents["blur"]) => {
      const { editor } = props;
      stopInlineEditing();
      if (editor) {
        const content = editor.getJSON();
        if (isRichTextContent(content)) {
          updateTextProp(content, "rich-text");
        }
      }
    },
    [stopInlineEditing, updateTextProp],
  );

  const htmlContent = useMemo(() => {
    if (isRichTextContent(text)) {
      return convertTipTapToHTML(text);
    }
    return null;
  }, [text]);

  if (isCurrentlyInlineEditing) {
    const editorValue = isRichTextContent(text)
      ? text
      : createTipTapFromText(text);
    return (
      <TipTapEditor
        value={editorValue}
        onBlur={handleBlur}
        placeholder={placeholder}
        shouldFocusWhenReady
      />
    );
  }

  if (htmlContent) {
    return (
      <div
        className={className}
        onDoubleClick={handleDoubleClick}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }

  return (
    <span className={className} onDoubleClick={handleDoubleClick}>
      {text || placeholder}
    </span>
  );
};
